import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  clearAuthToken,
  fetchCurrentUser,
  getAuthToken,
  loginUser,
  setAuthToken,
  signupUser,
  type SignupPayload
} from './apiClient';
import { clearAllRecoveryBuffers } from './recoveryBufferRegistry';
import type { User } from '../types';

interface AuthContextValue {
  user: User | null;
  token: string | null;
  loading: boolean;
  error: string | null;
  login: (payload: { email: string; password: string }) => Promise<void>;
  loginWithToken: (token: string) => Promise<void>;
  signup: (payload: SignupPayload) => Promise<void>;
  refreshUser: () => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);
const STORAGE_KEY = 'sitewise-token';
const LEGACY_STORAGE_KEY = 'inspectai-token';

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    const stored = typeof window !== 'undefined'
      ? window.localStorage.getItem(STORAGE_KEY) ?? window.localStorage.getItem(LEGACY_STORAGE_KEY)
      : null;
    if (stored) {
      window.localStorage.setItem(STORAGE_KEY, stored);
      window.localStorage.removeItem(LEGACY_STORAGE_KEY);
      setAuthToken(stored);
      setToken(stored);
      fetchCurrentUser()
        .then((currentUser) => {
          setUser(currentUser);
        })
        .catch(() => {
          window.localStorage.removeItem(STORAGE_KEY);
          window.localStorage.removeItem(LEGACY_STORAGE_KEY);
          clearAuthToken();
        })
        .finally(() => setLoading(false));
      return;
    }
    setLoading(false);
  }, []);

  const handleAuthSuccess = useCallback(
    async (authToken: string, authUser: User) => {
      setAuthToken(authToken);
      setToken(authToken);
      setUser(authUser);
      setError(null);
      window.localStorage.setItem(STORAGE_KEY, authToken);
      window.localStorage.removeItem(LEGACY_STORAGE_KEY);
      await queryClient.invalidateQueries();
    },
    [queryClient]
  );

  const login = useCallback(
    async (payload: { email: string; password: string }) => {
      try {
        setLoading(true);
        setError(null);
        const { token: authToken, user: authUser } = await loginUser(payload);
        await handleAuthSuccess(authToken, authUser);
        navigate('/app', { replace: true });
      } catch (err) {
        console.error(err);
        setError('Unable to log in with those credentials.');
        clearAuthToken();
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    },
    [handleAuthSuccess, navigate]
  );

  // OAuth callback path (D1): the backend hands the SPA an already-minted
  // access token; exchange it for the current user like a normal login.
  const loginWithToken = useCallback(
    async (authToken: string) => {
      try {
        setLoading(true);
        setError(null);
        setAuthToken(authToken);
        const currentUser = await fetchCurrentUser();
        await handleAuthSuccess(authToken, currentUser);
        navigate('/app', { replace: true });
      } catch (err) {
        console.error(err);
        clearAuthToken();
        setToken(null);
        setUser(null);
        navigate('/login?error=oauth_failed', { replace: true });
      } finally {
        setLoading(false);
      }
    },
    [handleAuthSuccess, navigate]
  );

  const refreshUser = useCallback(async () => {
    if (!token) return;
    try {
      const currentUser = await fetchCurrentUser();
      setUser(currentUser);
    } catch (err) {
      console.error(err);
    }
  }, [token]);

  const signup = useCallback(
    async (payload: SignupPayload) => {
      try {
        setLoading(true);
        setError(null);
        const { token: authToken, user: authUser, checkoutUrl } = await signupUser(payload);
        await handleAuthSuccess(authToken, authUser);
        // Organization + Pro (story 2-13): finish payment at Stripe. The org is
        // already usable on free, so this is an upgrade hop, not a gate.
        if (checkoutUrl) {
          window.location.assign(checkoutUrl);
          return;
        }
        navigate('/app', { replace: true });
      } catch (err) {
        console.error(err);
        setError('Unable to create account. Please try a different email.');
        clearAuthToken();
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    },
    [handleAuthSuccess, navigate]
  );

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    clearAuthToken();
    window.localStorage.removeItem(STORAGE_KEY);
    window.localStorage.removeItem(LEGACY_STORAGE_KEY);
    // P05 §9.4: the capture op buffer (and every future recovery buffer 5-18
    // consolidates) must not survive a sign-out into the next person's session
    // — a shared tablet on a job site is the case this exists for.
    clearAllRecoveryBuffers();
    queryClient.clear();
    navigate('/', { replace: true });
  }, [navigate, queryClient]);

  useEffect(() => {
    const tokenFromClient = getAuthToken();
    if (!token && tokenFromClient) {
      setToken(tokenFromClient);
    }
  }, [token]);

  const value = useMemo<AuthContextValue>(
    () => ({ user, token, loading, error, login, loginWithToken, signup, refreshUser, logout }),
    [user, token, loading, error, login, loginWithToken, signup, refreshUser, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
